// src/pages/CartPage.js
import React, { useEffect, useMemo, useState } from "react";
import ProductCard from "../components/ProductCard";
import CustomerInfoModal from "../components/CustomerInfoModal";
import { supabase } from "../supabaseClient";

const currency = (n) =>
  (n || 0).toLocaleString("es-CR", { style: "currency", currency: "CRC", minimumFractionDigits: 2 });

const CartPage = ({
  user,
  cartItems,
  onRemoveFromCart,
  onUpdateQuantity,
  onFinalizePurchase,
  onProductClick,
  onAddToCart,
  onNavigate,
}) => {
  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const userEmail = user?.correo || user?.email || '';

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );
  const totalItems = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const handleFinalizeClick = () => {
    if (cartItems.length === 0) return;
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setShowCustomerInfoModal(true);
  };

  // === Sugeridos ===
  useEffect(() => {
    const fetchRandomSuggested = async (excludeIds = new Set()) => {
      try {
        const { data: idRows, error: e1 } = await supabase.from("products").select("id");
        if (e1 || !idRows || idRows.length === 0) {
          console.error("Error trayendo IDs de productos:", e1);
          setSuggestedProducts([]);
          return;
        }

        const availableIds = idRows.map((r) => r.id).filter((id) => !excludeIds.has(id));
        if (availableIds.length === 0) {
          setSuggestedProducts([]);
          return;
        }

        // barajar y tomar 4
        const shuffled = [...availableIds].sort(() => Math.random() - 0.5);
        const pick = shuffled.slice(0, Math.min(4, shuffled.length));

        const { data: prods, error: e2 } = await supabase
          .from("products")
          .select("*")
          .in("id", pick);

        if (e2 || !prods) {
          console.error("Error trayendo productos aleatorios:", e2);
          setSuggestedProducts([]);
          return;
        }

        // conservar orden aleatorio
        const order = new Map(pick.map((id, i) => [id, i]));
        prods.sort((a, b) => order.get(a.id) - order.get(b.id));

        setSuggestedProducts(prods);
      } catch (err) {
        console.error("Fallo inesperado en sugeridos:", err);
        setSuggestedProducts([]);
      }
    };

    const fetchRelatedSuggested = async () => {
      const idsInCart = new Set(cartItems.map((item) => item.id));
      const subcategoryIds = Array.from(
        new Set(cartItems.map((item) => item.subcategory_id).filter((id) => id != null))
      );
      const categoryIds = Array.from(
        new Set(cartItems.map((item) => item.category_id).filter((id) => id != null))
      );

      try {
        let query = supabase.from("products").select("*").limit(12);
        if (subcategoryIds.length > 0) {
          query = query.in("subcategory_id", subcategoryIds);
        } else if (categoryIds.length > 0) {
          query = query.in("category_id", categoryIds);
        }

        const { data, error } = await query;
        if (error) {
          throw error;
        }

        const filtered = (data || [])
          .filter((product) => !idsInCart.has(product.id))
          .sort(() => Math.random() - 0.5);

        if (filtered.length === 0) {
          await fetchRandomSuggested(idsInCart);
          return;
        }

        setSuggestedProducts(filtered.slice(0, 4));
      } catch (err) {
        console.error("Fallo cargando relacionados del carrito:", err);
        await fetchRandomSuggested(idsInCart);
      }
    };

    if (cartItems.length === 0) {
      fetchRandomSuggested();
    } else {
      fetchRelatedSuggested();
    }
  }, [cartItems]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Tu Carrito</h1>

      {cartItems.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <p className="text-gray-700 mb-6">Tu carrito está vacío.</p>
          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-6">Productos Sugeridos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6">
            {suggestedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={onAddToCart}
                onProductClick={onProductClick}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Columna de Productos en el Carrito */}
          <div className="md:col-span-1 bg-white rounded-2xl shadow-lg p-6">
            <div className="space-y-4">
              {cartItems.map((item) => (
                <CartRow
                  key={item.id}
                  item={item}
                  onUpdateQuantity={onUpdateQuantity}
                  onRemoveFromCart={onRemoveFromCart}
                />
              ))}
            </div>
          </div>

          {/* Columna de Resumen */}
          <div className="md:col-span-1 bg-white rounded-2xl shadow-lg p-6 h-fit">
            <h2 className="text-xl font-semibold mb-4 text-gray-900">Resumen</h2>
            <div className="flex justify-between text-gray-700 mb-2">
              <span>Artículos</span>
              <span>{totalItems}</span>
            </div>
            <div className="flex justify-between text-gray-700 mb-4">
              <span>Subtotal</span>
              <span>{currency(subtotal)}</span>
            </div>
            <button
              onClick={handleFinalizeClick}
              className="w-full bg-brand text-white py-3 rounded-xl hover:bg-brand/90 transition-colors font-semibold"
            >
              Finalizar compra
            </button>
          </div>
        </div>
      )}

      {cartItems.length > 0 && suggestedProducts.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quizá te interese</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6">
            {suggestedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={onAddToCart}
                onProductClick={onProductClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal de info del cliente (si está logueado) */}
      {showCustomerInfoModal && user && (
        <CustomerInfoModal
          userId={user.id}
          userEmail={userEmail}
          onClose={() => setShowCustomerInfoModal(false)}
          onConfirm={(customerInfo) => {
            setShowCustomerInfoModal(false);
            onFinalizePurchase(customerInfo);
          }}
        />
      )}

      {/* Modal pidiendo iniciar sesión */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">¿Iniciar sesión?</h3>
            <p className="text-gray-600 mb-6">
              Para finalizar tu compra necesitamos tus datos. Inicia sesión o regístrate.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  onNavigate("/login");
                }}
                className="flex-1 bg-brand text-white py-2 rounded-lg hover:bg-brand/90"
              >
                Iniciar sesión
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================
//   Fila del carrito
// =====================
const CartRow = ({ item, onUpdateQuantity, onRemoveFromCart }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  const inc = () => onUpdateQuantity(item.id, item.quantity + 1);
  const dec = () => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1));

  return (
    <div className="flex items-center bg-gray-50 p-4 rounded-lg shadow-sm">
      <div className="relative w-24 h-24 mr-4">
        {!imgLoaded && <div className="absolute inset-0 bg-gray-200 rounded-md animate-pulse" />}
        <img
          src={item.image}
          alt={item.name}
          className={`w-24 h-24 object-cover rounded-md transition-opacity duration-300 ${
            imgLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
        />
      </div>

      <div className="flex-grow">
        <h3 className="font-semibold text-lg text-gray-900">{item.name}</h3>
        <p className="text-gray-700">{currency(item.price)}</p>
        <div className="flex items-center mt-2 gap-3">
          <button
            onClick={dec}
            className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300"
            aria-label="Disminuir cantidad"
          >
            −
          </button>
          <span className="w-8 text-center font-medium">{item.quantity}</span>
          <button
            onClick={inc}
            className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300"
            aria-label="Aumentar cantidad"
          >
            +
          </button>
          <button
            onClick={() => onRemoveFromCart(item.id)}
            className="ml-3 text-red-600 hover:text-red-700 text-sm"
          >
            Quitar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
